/**
 * Seed data (SCHEMA §10).
 *
 * Every figure here is transcribed from the source workbook and cross-checked against
 * `CLAUDE.md`. The totals are asserted in `seed.test.ts`, which is the M2 exit criterion:
 * seeded opening balances must match to the paise.
 *
 * Two dates matter and they are not the same:
 *
 *   · `openingBalanceDate` — **1 Aug 2026**, the cutover. Authoritative. Everything
 *     before it was reconciled by hand and must not be tallied forward (FR-ACC-06a).
 *   · `historicalOpeningDate` — 1 Jan 2026, or 1 Feb for an account opened later. Drives
 *     the migrated-months display only, never a current balance (FR-ACC-06b).
 */

import type { AccountType } from './accounts';
import type { DateKey } from './dates';
import { negate, type Paise } from './money';

export const CUTOVER: DateKey = '2026-08-01';
export const HISTORICAL_START: DateKey = '2026-01-01';

export interface SeedAccount {
  id: string;
  name: string;
  type: AccountType;
  /** Rupees as written in the source; converted to paise on use. */
  opening: number;
  historicalOpening?: number | null;
  historicalOpeningDate?: DateKey;
  /** FR-SAV-04: a scheme that redeems in goods, not money. */
  redemption?: 'cash' | 'in-kind';
  creditLimit?: number;
  statementDay?: number | null;
  dueDay?: number;
  note?: string;
}

/**
 * Banks and cash — the August sheet's Starting Balance column.
 * Totals ₹11,96,983.23, tying to the sheet's own H27.
 *
 * "Am Pay" appears in that panel at 0 but is deliberately absent here: it is Amazon Pay
 * Later, already present as two credit accounts. Creating it again would reproduce the
 * workbook's own balance-in-two-places defect.
 */
export const SEED_BANK_ACCOUNTS: SeedAccount[] = [
  { id: 'hdfc-current', name: 'HDFC Current', type: 'bank', opening: 485943.06, historicalOpening: 0, historicalOpeningDate: '2026-02-01', note: 'Opened Feb 2026; did not exist on 1 Jan' },
  { id: 'canara', name: 'Canara', type: 'bank', opening: 214534.46, historicalOpening: 6342.58 },
  { id: 'fi-federal', name: 'FI Federal', type: 'bank', opening: 141090.01, historicalOpening: 19899.81 },
  { id: 'icici-pers-demat', name: 'ICICI PERS+Demat', type: 'bank', opening: 94260.97, historicalOpening: 7717.60 },
  { id: 'icici-pers-ta', name: 'ICICI PERS TA', type: 'bank', opening: 79085.98, historicalOpening: 309880.92 },
  { id: 'sbm', name: 'SBM', type: 'bank', opening: 72974.34, historicalOpening: 6711.17 },
  { id: 'idfc', name: 'IDFC', type: 'bank', opening: 44094.75, historicalOpening: 78212.57 },
  { id: 'hdfc-sha', name: 'HDFC SHA', type: 'bank', opening: 19672.90, historicalOpening: 0 },
  { id: 'iob-icici-sa', name: 'IOB / ICICI SA', type: 'bank', opening: 13251.00, historicalOpening: 10845.99 },
  { id: 'cub', name: 'CUB', type: 'bank', opening: 10162.00, historicalOpening: 1306.99 },
  { id: 'dcb', name: 'DCB', type: 'bank', opening: 5997.51, historicalOpening: 5831.51 },
  { id: 'cash', name: 'Cash', type: 'cash', opening: 5220.00, historicalOpening: 150.00 },
  { id: 'sbi', name: 'SBI', type: 'bank', opening: 5024.01, historicalOpening: 11398.53 },
  { id: 'equitas', name: 'Equitas', type: 'bank', opening: 2408.00, historicalOpening: 11798.64 },
  { id: 'kotak', name: 'Kotak', type: 'bank', opening: 1847.00, historicalOpening: 127866.66 },
  { id: 'dcb-niyo', name: 'DCB Niyo', type: 'bank', opening: 1417.24, historicalOpening: 1181.05 },
  { id: 'icici-corp', name: 'ICICI CORP', type: 'bank', opening: 0, historicalOpening: 845.31 },
  { id: 'pk-icici', name: 'PK ICICI', type: 'bank', opening: 0, historicalOpening: 0 },
];

/**
 * Cards — opening outstanding at 1 Aug 2026, read from the Credits blocks.
 * Totals ₹1,93,924.31; the three rows dated 1 Aug or later import as transactions.
 *
 * `opening` is written positive here for legibility against the source, and negated on
 * conversion: liabilities are stored negative (SCHEMA §11.2).
 */
export const SEED_CARD_ACCOUNTS: SeedAccount[] = [
  { id: 'icici-cc-coral', name: 'ICICI CC Coral', type: 'credit_card', opening: 98333.84, creditLimit: 99000, statementDay: 8, dueDay: 23 },
  { id: 'idfc-cc-ta', name: 'IDFC CC TA', type: 'credit_card', opening: 47425.52, creditLimit: 100000, statementDay: 20, dueDay: 5 },
  { id: 'sbm-cc', name: 'SBM CC', type: 'credit_card', opening: 26304.96, creditLimit: 49500, statementDay: 2, dueDay: 17 },
  { id: 'rbl-cc', name: 'RBL CC', type: 'credit_card', opening: 21859.99, creditLimit: 80000, statementDay: 12, dueDay: 27 },
  { id: 'hdfc-cc-sha', name: 'HDFC CC Sha', type: 'credit_card', opening: 0, creditLimit: 198000, statementDay: 20, dueDay: 5 },
  { id: 'amazon-pay-vv', name: 'Amazon Pay VV', type: 'credit_card', opening: 0, creditLimit: 80000, statementDay: null, dueDay: 1, note: 'Amazon Pay Later — no statement date' },
  { id: 'amazon-pay-sha', name: 'Amazon Pay Sha', type: 'credit_card', opening: 0, creditLimit: 25000, statementDay: null, dueDay: 1, note: 'Amazon Pay Later — no statement date' },
];

/**
 * Savings pots and investments.
 *
 * TA Savings derives from eight dated contributions on the Credits sheet. The FDs total
 * ₹3,30,000 as recorded in `CLAUDE.md`. **Jchit and GRT Pay have no stated position in
 * any source** — they seed at zero and are flagged, rather than guessed at.
 */
export const SEED_SAVINGS_ACCOUNTS: SeedAccount[] = [
  { id: 'ta-savings', name: 'TA Savings', type: 'savings_pot', opening: 38000 },
  { id: 'jchit', name: 'Jchit', type: 'savings_pot', opening: 0, note: 'Opening position unknown — not stated in any source' },
  { id: 'grt-pay', name: 'GRT Pay', type: 'savings_pot', opening: 0, redemption: 'in-kind', note: 'Opening position unknown; redemption is in kind, never cash' },
  { id: 'hdfc-fd', name: 'HDFC FD', type: 'investment', opening: 170000 },
  { id: 'icici-fd', name: 'ICICI FD', type: 'investment', opening: 110000 },
  { id: 'fd-booking', name: 'FD Booking', type: 'investment', opening: 50000 },
];

/**
 * The loan account. Its detail record, schedule and foreclosure calculator are M7; the
 * account exists from M2 so import can collapse "Car Loan" and "Mahindra Fin LL" onto
 * one target (IMPLEMENTATION-PLAN §1.1).
 */
export const SEED_LOAN_ACCOUNTS: SeedAccount[] = [
  { id: 'mahindra-car-loan', name: 'Car Loan — Mahindra', type: 'loan', opening: 878750, note: 'Outstanding principal after 11 EMIs' },
];

export const SEED_ACCOUNTS: SeedAccount[] = [
  ...SEED_BANK_ACCOUNTS,
  ...SEED_CARD_ACCOUNTS,
  ...SEED_SAVINGS_ACCOUNTS,
  ...SEED_LOAN_ACCOUNTS,
];

// ---------------------------------------------------------------------------

export interface SeededAccount {
  id: string;
  name: string;
  type: AccountType;
  currency: 'INR';
  archived: false;
  openingBalancePaise: Paise;
  openingBalanceDate: DateKey;
  balancePaise: Paise;
  historicalOpeningPaise: Paise | null;
  historicalOpeningDate: DateKey | null;
  minBalancePaise: null;
  transactionCount: 0;
  sortOrder: number;
  creditLimitPaise?: Paise;
  statementDay?: number | null;
  dueDay?: number;
  dueDayIsNextMonth?: boolean;
  loanId?: string;
  redemption?: 'cash' | 'in-kind';
  note?: string;
}

const toPaise = (rupees: number): Paise => Math.round(rupees * 100);

/**
 * A due day earlier than the statement day means the bill falls due in the *following*
 * month — IDFC and HDFC bill on the 20th and take payment on the 5th. Derived rather
 * than hand-entered, so the two fields cannot contradict each other.
 */
export function dueFallsNextMonth(statementDay: number | null | undefined, dueDay: number): boolean {
  if (statementDay == null) return true;
  return dueDay < statementDay;
}

export function buildSeedAccount(seed: SeedAccount, index: number): SeededAccount {
  const liability = seed.type === 'credit_card' || seed.type === 'loan';
  const account: SeededAccount = {
    id: seed.id,
    name: seed.name,
    type: seed.type,
    currency: 'INR',
    archived: false,
    // `negate`, not `-`: a card with nothing outstanding would otherwise seed as -0,
    // which Firestore stores as a double and the `is int` rule then rejects.
    openingBalancePaise: liability ? negate(toPaise(seed.opening)) : toPaise(seed.opening),
    openingBalanceDate: CUTOVER,
    // No transactions yet, so the current balance is the opening one.
    balancePaise: liability ? negate(toPaise(seed.opening)) : toPaise(seed.opening),
    historicalOpeningPaise:
      seed.historicalOpening == null ? null : toPaise(seed.historicalOpening),
    historicalOpeningDate:
      seed.historicalOpening == null
        ? null
        : (seed.historicalOpeningDate ?? HISTORICAL_START),
    minBalancePaise: null,
    transactionCount: 0,
    sortOrder: index,
  };

  if (seed.type === 'credit_card') {
    account.creditLimitPaise = toPaise(seed.creditLimit ?? 0);
    account.statementDay = seed.statementDay ?? null;
    account.dueDay = seed.dueDay ?? 1;
    account.dueDayIsNextMonth = dueFallsNextMonth(seed.statementDay, seed.dueDay ?? 1);
  }
  if (seed.type === 'loan') account.loanId = seed.id;
  if (seed.redemption) account.redemption = seed.redemption;
  if (seed.note) account.note = seed.note;

  return account;
}

export function buildSeedAccounts(): SeededAccount[] {
  return SEED_ACCOUNTS.map(buildSeedAccount);
}
